import { useState, useEffect, useMemo, useRef } from 'react';
import { Box, Paper, Typography, Chip, Button, IconButton } from '@mui/material';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useRoleList } from '../../../stores/roleStore';
import { useCompanyList } from '../../../stores/companyStore';
import { useVehicleStatusDashboardDynamic } from '../../../stores/vehicleStatusStore';
import StreamingInfo from './StreamingInfo';
import { useNotifier } from '../../../components/Notifier';
import { useLanguage } from '../../../utils/i18n';
import InformationBubbleDialog from '../../../components/InformationBubbleDialog';

export default function LiveStreamingPageContent() {
  const { t } = useLanguage();
  const { roleList } = useRoleList();
  const { companyOptions } = useCompanyList();
  const [userInfo, setUserInfo] = useState(null);
  const [openInfo, setOpenInfo] = useState(false);
  const notify = useNotifier();

  useEffect(() => {
    try {
      const raw = localStorage.getItem('user');
      if (raw) setUserInfo(JSON.parse(raw));
    } catch (e) {
      console.log(e);
    }
  }, []);

  const { data: vehicleList = [] } = useVehicleStatusDashboardDynamic(100, 7);

  const [activeStreams, setActiveStreams] = useState([]);

  const handleSelectStream = (newStreamData) => {
    if (!newStreamData.vehicle || !newStreamData.vehicle.id) return;

    setActiveStreams((prev) => {
      const exists = prev.find((s) => s.vehicle.id === newStreamData.vehicle.id);

      const newChannels = newStreamData.chs ? newStreamData.chs.split('_').length : 1;
      let otherChannelsCount = 0;
      prev.forEach((s) => {
        if (s.vehicle.id !== newStreamData.vehicle.id) {
          otherChannelsCount += s.chs ? s.chs.split('_').length : 1;
        }
      });

      if (otherChannelsCount + newChannels > 16) {
        notify({ message: 'Max 16 selected camera', severity: 'error' });
        return prev;
      }

      if (exists) {
        // Update if already exists (e.g. changed camera channels)
        return prev.map((s) => (s.vehicle.id === newStreamData.vehicle.id ? newStreamData : s));
      }
      return [newStreamData, ...prev]; // Add to beginning
    });
  };

  const handleRemoveStream = (vehicleId) => {
    setActiveStreams((prev) => prev.filter((s) => s.vehicle.id !== vehicleId));
  };

  // Flatten all selected cameras from all selected vehicles
  const activeCameras = useMemo(() => {
    let cams = [];
    activeStreams.forEach((stream) => {
      const channels = stream.chs ? stream.chs.split('_') : ['1'];
      channels.forEach((ch) => {
        cams.push({
          id: `${stream.vehicle.id}-${ch}`,
          vehicle: stream.vehicle,
          deviceId: stream.deviceId,
          chs: ch,
          token: stream.token,
          baseUrl: stream.baseUrl
        });
      });
    });
    return cams;
  }, [activeStreams]);

  // Determine optimal grid layout (1x1, 2x2, 3x3, 4x4)
  const totalCams = activeCameras.length;
  const gridCount = totalCams === 0 ? 1 : totalCams <= 4 ? 4 : totalCams <= 9 ? 9 : 16;
  const cols = Math.sqrt(gridCount);

  const gridCells = Array.from({ length: gridCount }).map((_, index) => {
    return activeCameras[index] || null;
  });

  // Fullscreen Feature
  const gridRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      gridRef.current?.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable full-screen mode: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 2, height: 'calc(100vh - 150px)' }}>
        {/* Konten Video Utama */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Header Kendaraan Aktif */}
          <Paper
            elevation={1}
            sx={{ p: 1, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="body2" fontWeight="bold" color="text.secondary" sx={{ mr: 1 }}>
                {t('liveStreaming.activeCamera', 'Kamera Aktif')} ({totalCams}/16):
              </Typography>
              {activeStreams.length === 0 && (
                <Typography variant="caption" color="text.disabled">
                  {t('liveStreaming.noCameraSelected', 'Belum ada kamera terpilih')}
                </Typography>
              )}
              {activeStreams.map((stream) => (
                <Chip
                  key={stream.vehicle.id}
                  label={stream.vehicle.fleet_name || stream.deviceId}
                  onDelete={() => handleRemoveStream(stream.vehicle.id)}
                  color="primary"
                  variant="outlined"
                  size="small"
                  sx={{ fontWeight: 'bold' }}
                />
              ))}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={() => setOpenInfo(true)} sx={{ bgcolor: '#e3f2fd', color: '#0374DA', borderRadius: 2 }} size="small">
                <InfoOutlinedIcon />
              </IconButton>
              <Button
                variant="contained"
                size="small"
                startIcon={isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                onClick={toggleFullscreen}
                disabled={activeStreams.length === 0}
                sx={{ bgcolor: '#485D9A', '&:hover': { bgcolor: '#3b4d82' } }}
              >
                {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
              </Button>
            </Box>
          </Paper>

          {/* CCTV Grid View */}
          <Paper
            ref={gridRef}
            sx={{
              width: '100%',
              flexGrow: 1,
              bgcolor: '#000',
              borderRadius: 2,
              overflow: 'hidden',
              display: 'grid',
              gridTemplateColumns: `repeat(${cols}, 1fr)`,
              gridTemplateRows: `repeat(${cols}, 1fr)`,
              gap: '2px', // Thin border between cameras
              p: '2px'
            }}
          >
            {gridCells.map((cam, idx) => (
              <Box key={idx} sx={{ bgcolor: '#111', position: 'relative', width: '100%', height: '100%' }}>
                {cam ? (
                  <>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        p: 0.5,
                        bgcolor: 'rgba(0,0,0,0.5)',
                        zIndex: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <Typography variant="caption" sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.7rem', px: 1 }}>
                        {cam.vehicle.fleet_name} - CH{cam.chs}
                      </Typography>
                    </Box>
                    <iframe
                      src={`${cam.baseUrl}?token=${cam.token}&deviceId=${cam.deviceId}&chs=${cam.chs}&stream=0&wnum=1&panel=1&buffer=2000`}
                      title={`Live Camera ${cam.id}`}
                      width="100%"
                      height="100%"
                      style={{ border: 'none', position: 'absolute', top: 0, left: 0 }}
                      allowFullScreen
                    />
                  </>
                ) : (
                  <Box
                    sx={{ display: 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', opacity: 0.2 }}
                  >
                    <VideocamOutlinedIcon sx={{ color: '#fff', fontSize: 40 }} />
                  </Box>
                )}
              </Box>
            ))}
          </Paper>
        </Box>

        {/* Sidebar Info */}
        <Box sx={{ width: { xs: '100%', lg: '350px' }, flexShrink: 0 }}>
          <StreamingInfo
            vehicleList={vehicleList}
            roleList={roleList}
            userInfo={userInfo}
            companyOptions={companyOptions}
            onSelectStream={handleSelectStream}
            activeStreams={activeStreams}
          />
        </Box>
      </Box>
      <InformationBubbleDialog
        open={openInfo}
        onClose={() => setOpenInfo(false)}
        title={t('answer.titleLiveStreaming', 'Live Streaming Guide')}
        messages={[
          { type: 'q', text: t('question.liveStreaming1', 'How do I start viewing a live stream?') },
          {
            type: 'a',
            text: t(
              'answer.liveStreaming1',
              'Simply click on any vehicle card in the list! By default, it will instantly show Camera Channel 1 from that vehicle.'
            )
          },
          { type: 'q', text: t('question.liveStreaming2', 'Can I view specific cameras or multiple cameras at once?') },
          {
            type: 'a',
            text: t(
              'answer.liveStreaming2',
              'Yes, absolutely! You can check the specific camera boxes before clicking the vehicle card. The system will automatically display all your selected cameras.'
            )
          },
          { type: 'q', text: t('question.liveStreaming3', 'Is there a limit to how many cameras I can watch?') },
          {
            type: 'a',
            text: t(
              'answer.liveStreaming3',
              'You can watch up to 16 cameras simultaneously. For the best experience, you can also use the Fullscreen button to expand the view!'
            )
          },
          { type: 'q', text: t('question.liveStreaming4', 'Where can I find my downloaded videos?') },
          {
            type: 'a',
            text: t(
              'answer.liveStreaming4',
              'The results of the video downloads can be viewed in the History Today and History Download Video Page.'
            )
          }
        ]}
      />
    </>
  );
}
